import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAllProject, deleteProject, createProject } from '../../store/slices/projectSlice';
import { deleteFile } from '../../store/slices/embeddingSlice'; // Import deleteFile
import Button from '../../components/button';
import './dashboard.scss';
import BotIcon from '../../assets/icons/bot.svg';
import EditIcon from '../../assets/icons/edit.svg';
import DeleteIcon from '../../assets/icons/delete-icons.svg';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import Knowledgesources from '../stats/knowledgesources';
import Createknowledgesource from '../../components/modal/createknowledgesource';
import Conformation from '../../components/modal/conformation'; // Import Confirmation Modal

export default function Dashboard() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { project } = useSelector((state) => state.project);
    const { user } = useSelector((state) => state.auth);
    const [isLoading, setIsLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedBot, setSelectedBot] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newBotName, setNewBotName] = useState('');

    // State for Knowledge Source Modal & Deletion
    const [createknowledgesource, setCreateknowledgesource] = useState(false);
    const [deleteKnowledgeSource, setDeleteKnowledgeSource] = useState(false);
    const [deletedData, setDeletedData] = useState({});

    const fetchProjects = React.useCallback(async () => {
        setIsLoading(true);
        try {
            await dispatch(getAllProject({}));
        } catch (error) {
            toast.error('Failed to load chatbots');
        } finally {
            setIsLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    // Handle Knowledge Source Deletion
    const handleDeleteFile = (data) => {
        let body = {
            id: data?.id,
        };
        dispatch(deleteFile(body))
            .unwrap()
            .then((res) => {
                if (!res?.data?.success) {
                    toast.error(res?.data?.message);
                    return;
                }
                toast.success(res?.data?.message);
                setDeleteKnowledgeSource(false);
                // Refresh list happens via Redux state update usually, or we might need to trigger fetch
                // But embeddingSlice often handles optimistic updates or re-fetches depending on implementation.
                // Assuming embeddingSlice updates 'embeddingFile' state on success.
            });
    };

    const handleDeleteClick = (bot) => {
        setSelectedBot(bot);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!selectedBot) return;
        try {
            const result = await dispatch(deleteProject({ id: selectedBot.id }));
            if (result.payload?.data?.success) {
                toast.success('Chatbot deleted successfully');
                fetchProjects();
            } else {
                toast.error(result.payload?.data?.message || 'Failed to delete chatbot');
            }
        } catch (error) {
            toast.error('Failed to delete chatbot');
        } finally {
            setShowDeleteModal(false);
            setSelectedBot(null);
        }
    };

    const handleEditClick = (bot) => {
        navigate(`/project/${bot.id}/settings`);
    };

    const handleCreateBot = async () => {
        if (!newBotName.trim()) {
            toast.error('Please enter a bot name');
            return;
        }
        try {
            const result = await dispatch(createProject({
                bot_name: newBotName,
                company_name: newBotName,
                description: ''
            }));
            if (result.payload?.data?.success) {
                toast.success('Chatbot created successfully');
                setShowCreateModal(false);
                setNewBotName('');
                fetchProjects();
            } else {
                toast.error(result.payload?.data?.message || 'Failed to create chatbot');
            }
        } catch (error) {
            toast.error('Failed to create chatbot');
        }
    };

    // Determine view based on URL param or path
    const { tab } = useParams();
    // UseEffect to reset knowledge source modal on tab change if needed

    return (
        <div className="dashboard-container">
            {tab === 'knowledge' ? (
                <div className="global-knowledge-wrapper">
                    <Knowledgesources
                        isGlobal={true}
                        createknowledgesource={createknowledgesource}
                        setCreateknowledgesource={setCreateknowledgesource}
                        deleteKnowledgeSource={deleteKnowledgeSource}
                        setDeleteKnowledgeSource={setDeleteKnowledgeSource}
                        deletedData={deletedData}
                        setDeletedData={setDeletedData}
                    />
                    {/* Render Create Modal */}
                    {createknowledgesource && (
                        <Createknowledgesource
                            createknowledgesource={createknowledgesource}
                            setCreateknowledgesource={setCreateknowledgesource}
                        />
                    )}
                    {/* Render Delete Confirmation Modal */}
                    <Conformation
                        isModal={deleteKnowledgeSource}
                        title={"Delete"}
                        onClose={() => setDeleteKnowledgeSource(!deleteKnowledgeSource)}
                        onClick={() => handleDeleteFile(deletedData)}
                        message={`Are you sure, you want to delete ${deletedData?.input_file_name}?`}
                        btnText={"Delete"}
                        icon={DeleteIcon}
                    />
                </div>
            ) : tab === 'overview' ? (
                <div className="global-overview">
                    <div className="dashboard-header">
                        <h1>Dashboard Overview</h1>
                    </div>
                    <div className="stats-cards">
                        <div className="stat-card">
                            <h3>Total Chatbots</h3>
                            <p className="stat-value">{project?.length || 0}</p>
                        </div>
                        <div className="stat-card">
                            <h3>Total Documents</h3>
                            <p className="stat-value">
                                {project?.reduce((acc, curr) => acc + (curr.embedding_count || 0), 0)}
                            </p>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="dashboard-header">
                        <h1>My Chatbots</h1>
                        <Button
                            text="+ Create Chatbot"
                            onClick={() => setShowCreateModal(true)}
                        />
                    </div>
                    {isLoading ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading your chatbots...</p>
                        </div>
                    ) : project?.length > 0 ? (
                        <div className="chatbots-grid">
                            {project.map((bot) => (
                                <div key={bot.id} className="chatbot-card" onClick={() => navigate(`/project/${bot.id}/settings`)} style={{ cursor: 'pointer' }}>
                                    {/* Bot Card Content */}
                                    <div className="card-header">
                                        <div className="bot-icon" style={{ background: bot.base_color || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                            <img src={bot.bot_image || BotIcon} alt="Bot" />
                                        </div>
                                        <div className="status-badge" data-status={bot.is_published ? 'active' : 'inactive'}>
                                            {bot.is_published ? 'Active' : 'Inactive'}
                                        </div>
                                    </div>

                                    <div className="card-body">
                                        <h3>{bot.bot_name || 'Unnamed Bot'}</h3>
                                        <p className="company-name">{bot.company_name}</p>
                                        <div className="meta-info">
                                            <span className="doc-count">
                                                {bot.embedding_count || 0} Documents
                                            </span>
                                            <span className="created-date">
                                                Created {moment(bot.created_at).format('MMM D, YYYY')}
                                            </span>
                                        </div>
                                        {/* Admin Access Badge */}
                                        {user?.id && bot.user_id && user.id !== bot.user_id && (
                                            <>
                                                <div className="admin-access-badge">
                                                    Admin Access
                                                </div>
                                                {bot.user_email && (
                                                    <div className="user-email-badge">
                                                        <span>User:</span> {bot.user_email}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    <div className="card-actions">
                                        <button className="action-btn edit" onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditClick(bot);
                                        }}>
                                            <img src={EditIcon} alt="Edit" />
                                            <span>Edit</span>
                                        </button>
                                        <button className="action-btn delete" onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteClick(bot);
                                        }}>
                                            <img src={DeleteIcon} alt="Delete" />
                                            <span>Delete</span>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <div className="empty-icon">
                                <img src={BotIcon} alt="No bots" />
                            </div>
                            <h2>No Chatbots Yet</h2>
                            <p>Create your first AI chatbot to get started</p>
                            <Button
                                text="Create Your First Chatbot"
                                onClick={() => setShowCreateModal(true)}
                            />
                        </div>
                    )}
                </>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Delete Chatbot</h2>
                        <p>Are you sure you want to delete <strong>{selectedBot?.bot_name}</strong>? This action cannot be undone.</p>
                        <div className="modal-actions">
                            <button className="btn-cancel" onClick={() => setShowDeleteModal(false)}>
                                Cancel
                            </button>
                            <button className="btn-delete" onClick={confirmDelete}>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Bot Modal */}
            {showCreateModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Create New Chatbot</h2>
                        <div className="form-group">
                            <label>Bot Name</label>
                            <input
                                type="text"
                                value={newBotName}
                                onChange={(e) => setNewBotName(e.target.value)}
                                placeholder="Enter bot name"
                            />
                        </div>
                        <div className="modal-actions">
                            <button className="btn-cancel" onClick={() => {
                                setShowCreateModal(false);
                                setNewBotName('');
                            }}>
                                Cancel
                            </button>
                            <button className="btn-create" onClick={handleCreateBot}>
                                Create
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
